import { Droppable } from "@hello-pangea/dnd";
import clsx from "clsx";
import { EllipsisVertical, Pen, Trash2 } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { DocListCategoryInput } from "./DocListCategoryInput";

interface NewCategoryPlaceholderProps {
  id: number;
  color: string;
  name: string;
  onRename: (id: number, newName: string) => void;
  onDelete: (id: number) => void;
}

const CategoryActions: FC<{
  onRename: () => void;
  onDelete: () => void;
}> = ({ onRename, onDelete }) => {
  return (
    <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-transparent">
            <EllipsisVertical className="h-4 w-4 text-white" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onRename}>
            <Pen className="h-4 w-4 mr-2" />
            <FormattedMessage
              id="docList.category.actionsDropdown.rename"
              defaultMessage="Rename"
            />
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onDelete} destructive>
            <Trash2 className="h-4 w-4 mr-2" />
            <FormattedMessage
              id="docList.category.actionsDropdown.delete"
              defaultMessage="Delete"
            />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export const NewCategoryPlaceholder: FC<NewCategoryPlaceholderProps> = ({
  id,
  color,
  name,
  onRename,
  onDelete,
}) => {
  const [isRenaming, setIsRenaming] = useState(false);

  if (isRenaming) {
    return (
      <DocListCategoryInput
        isNew
        color={color}
        oldName={name}
        organizationId=""
        projectId=""
        updatedAt={undefined}
        onUpdatedAtChange={() => {}}
        onDoclistOutdated={() => {}}
        onCancel={() => setIsRenaming(false)}
        onSubmit={(newName) => {
          onRename(id, newName);
          setIsRenaming(false);
        }}
      />
    );
  }

  return (
    <div className="relative mb-2 flex flex-col gap-[2px] group">
      <div
        className="h-[36px] w-full rounded-md px-3 py-2 flex items-center"
        style={{
          background: `linear-gradient(90deg, rgba(255, 255, 255, 0.5) 5.24%, rgba(0, 0, 0, 0.25) 93.21%), ${color}`,
          opacity: 0.5,
        }}
      >
        <span className="text-sm text-base-foreground uppercase">{name}</span>
        <CategoryActions
          onRename={() => setIsRenaming(true)}
          onDelete={() => onDelete(id)}
        />
      </div>
      <Droppable droppableId={`new-category-${id}`} type="section">
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={clsx(
              "h-[36px] w-full rounded-md px-3 py-2 border border-dashed border-base flex items-center transition-colors duration-200",
              snapshot.isDraggingOver ? "bg-blue-50" : "bg-transparent"
            )}
          >
            {!snapshot.isDraggingOver && (
              <span className="text-sm text-muted-foreground w-full min-w-full transition-all duration-200">
                <FormattedMessage
                  id="docList.categoryCreated"
                  defaultMessage="Category created, drag sections or pages here to activate it."
                />
              </span>
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};
